import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Select,
  MenuItem,
  Switch,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TuneIcon from '@mui/icons-material/Tune';
import SearchIcon from '@mui/icons-material/Search';
import CategoryIcon from '@mui/icons-material/Category';
import { toast } from 'react-toastify';
import config from '../../config';

/* ──────────────────────────────────────────────────────────────────────────
   QR ACCESS SETTINGS
   A self-contained panel embedded at the top of the QR Code Generator page.
   Lets the admin set each table's QR Access Mode (View Menu Only / Scan & Order)
   and enable/disable the QR — saved per table via /api/qrmenu/table-settings.
   The two extra modes are shown as disabled, future-ready placeholders.
   Touches nothing in the existing QR generator below it.
   ────────────────────────────────────────────────────────────────────────── */

const ACCESS_MODES = [
  { value: 'ViewOnly', label: 'View Menu Only', enabled: true },
  { value: 'ScanOrder', label: 'Scan & Order', enabled: true },
  { value: 'OrderPay', label: 'Order & Pay Online (soon)', enabled: false },
  { value: 'OrderWaiter', label: 'Order to Waiter (soon)', enabled: false },
];

export default function QrAccessSettings() {
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const token = sessionStorage.getItem('token');
  const companyId = company?.id ?? company?.companyId ?? company?.CompanyId;

  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [search, setSearch] = useState('');

  // Per-table category-visibility dialog.
  const [catDialog, setCatDialog] = useState(null); // { table, cats:[{id,name,visible}] }
  const [catLoading, setCatLoading] = useState(false);
  const [catSaving, setCatSaving] = useState(false);

  // Per-area online-payment toggle.
  const [areas, setAreas] = useState([]);
  const [areaSavingId, setAreaSavingId] = useState(null);

  const authHeaders = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const fetchTables = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/qrmenu/tables/${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await res.json();
      setTables(Array.isArray(data) ? data : []);
    } catch {
      setTables([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const fetchAreas = useCallback(async () => {
    if (!companyId) return;
    try {
      const res = await fetch(`${config.API_BASE_URL}/qrmenu/areas/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setAreas(Array.isArray(data) ? data : []);
    } catch {
      setAreas([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    fetchTables();
    fetchAreas();
  }, [fetchTables, fetchAreas]);

  const saveAreaPayment = async (area, enabled) => {
    const prev = areas;
    setAreas((rows) =>
      rows.map((a) => (a.id === area.id ? { ...a, paymentEnabled: enabled } : a)),
    );
    setAreaSavingId(area.id);
    try {
      const res = await fetch(`${config.API_BASE_URL}/qrmenu/area-payment`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ AreaId: area.id, PaymentEnabled: enabled }),
      });
      if (!res.ok) throw new Error();
      toast.success(
        `Online payment ${enabled ? 'ON' : 'OFF'} · ${area.areaName}`,
      );
    } catch {
      setAreas(prev);
      toast.error('Failed to save payment setting');
    } finally {
      setAreaSavingId(null);
    }
  };

  // Optimistic save — update the row immediately, revert on failure.
  const save = async (table, patch) => {
    const prev = tables;
    setTables((rows) =>
      rows.map((r) => (r.id === table.id ? { ...r, ...patch } : r)),
    );
    setSavingId(table.id);
    try {
      const res = await fetch(`${config.API_BASE_URL}/qrmenu/table-settings`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          Id: table.id,
          AccessMode: patch.accessMode ?? table.accessMode,
          IsQrEnabled: patch.isQrEnabled ?? table.isQrEnabled,
        }),
      });
      if (!res.ok) throw new Error();
      toast.success(`Saved · ${table.tableName}`);
    } catch {
      setTables(prev); // revert
      toast.error('Failed to save QR settings');
    } finally {
      setSavingId(null);
    }
  };

  const filtered = tables.filter((t) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (t.tableName || '').toLowerCase().includes(q) ||
      (t.areaName || '').toLowerCase().includes(q)
    );
  });

  // ── Category visibility per table ──
  const openCategories = async (table) => {
    setCatDialog({ table, cats: [] });
    setCatLoading(true);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/qrmenu/table-categories/${companyId}/${table.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const cats = await res.json();
      setCatDialog({ table, cats: Array.isArray(cats) ? cats : [] });
    } catch {
      setCatDialog({ table, cats: [] });
      toast.error('Failed to load categories');
    } finally {
      setCatLoading(false);
    }
  };

  const toggleCat = (id) =>
    setCatDialog((d) => ({
      ...d,
      cats: d.cats.map((c) =>
        c.id === id ? { ...c, visible: !c.visible } : c,
      ),
    }));

  const saveCategories = async () => {
    if (!catDialog) return;
    setCatSaving(true);
    try {
      const hidden = catDialog.cats.filter((c) => !c.visible).map((c) => c.id);
      const res = await fetch(
        `${config.API_BASE_URL}/qrmenu/table-categories`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            CompanyId: companyId,
            TableId: catDialog.table.id,
            HiddenCategoryIds: hidden,
          }),
        },
      );
      if (!res.ok) throw new Error();
      toast.success(`Categories saved · ${catDialog.table.tableName}`);
      setCatDialog(null);
    } catch {
      toast.error('Failed to save categories');
    } finally {
      setCatSaving(false);
    }
  };

  return (
    <Accordion defaultExpanded sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TuneIcon color='primary' />
          <Typography fontWeight={700}>QR Access Settings (per table)</Typography>
          <Chip
            size='small'
            label={`${tables.length} tables`}
            sx={{ ml: 1 }}
            color='primary'
            variant='outlined'
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
          Choose what a guest can do after scanning each table's QR. “View Menu
          Only” hides all ordering; “Scan & Order” lets them order. Disable a QR
          to show a closed notice instead of the menu.
        </Typography>

        <TextField
          size='small'
          placeholder='Search table / area'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 1.5, maxWidth: 320 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon fontSize='small' />
              </InputAdornment>
            ),
          }}
        />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : filtered.length === 0 ? (
          <Typography color='text.secondary' sx={{ py: 2 }}>
            No tables found. Add tables in “Create Area” first.
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Table</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Area</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Access Mode</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align='center'>
                    QR Enabled
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align='center'>
                    Categories
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((t) => (
                  <TableRow key={t.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{t.tableName}</TableCell>
                    <TableCell>{t.areaName || '—'}</TableCell>
                    <TableCell>
                      <Select
                        size='small'
                        value={t.accessMode || 'ScanOrder'}
                        disabled={savingId === t.id}
                        onChange={(e) =>
                          save(t, { accessMode: e.target.value })
                        }
                        sx={{ minWidth: 200 }}>
                        {ACCESS_MODES.map((m) => (
                          <MenuItem
                            key={m.value}
                            value={m.value}
                            disabled={!m.enabled}>
                            {m.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell align='center'>
                      <Switch
                        checked={t.isQrEnabled !== false}
                        disabled={savingId === t.id}
                        onChange={(e) =>
                          save(t, { isQrEnabled: e.target.checked })
                        }
                      />
                    </TableCell>
                    <TableCell align='center'>
                      <Button
                        size='small'
                        variant='outlined'
                        startIcon={<CategoryIcon />}
                        onClick={() => openCategories(t)}>
                        Manage
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {/* ── Online payment (per area) ── */}
        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #eef0f3' }}>
          <Typography fontWeight={700} sx={{ mb: 0.5 }}>
            Online Payment (per area)
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
            Turn ON to let guests pay online (CC Avenue / AllUPI) when they place
            an order from any table in that area. OFF = pay at counter (default).
          </Typography>
          {areas.length === 0 ? (
            <Typography color='text.secondary' variant='body2'>
              No areas found. Add areas in “Create Area” first.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {areas.map((a) => (
                <Box
                  key={a.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    minWidth: 240,
                    border: '1px solid #e8eef7',
                    borderRadius: 2,
                    px: 1.5,
                    py: 1,
                  }}>
                  <Box>
                    <Typography fontWeight={600}>{a.areaName}</Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {a.tableCount} tables
                    </Typography>
                  </Box>
                  <Switch
                    checked={a.paymentEnabled === true}
                    disabled={areaSavingId === a.id}
                    onChange={(e) => saveAreaPayment(a, e.target.checked)}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </AccordionDetails>

      {/* Per-table category visibility */}
      <Dialog
        open={!!catDialog}
        onClose={() => setCatDialog(null)}
        fullWidth
        maxWidth='xs'>
        <DialogTitle sx={{ fontWeight: 800 }}>
          Categories · {catDialog?.table?.tableName}
          <Typography variant='body2' color='text.secondary'>
            Only ticked categories appear after scanning this table's QR.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {catLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={26} />
            </Box>
          ) : (catDialog?.cats || []).length === 0 ? (
            <Typography color='text.secondary'>No categories found.</Typography>
          ) : (
            (catDialog?.cats || []).map((c) => (
              <FormControlLabel
                key={c.id}
                sx={{ display: 'flex' }}
                control={
                  <Checkbox
                    checked={c.visible !== false}
                    onChange={() => toggleCat(c.id)}
                  />
                }
                label={c.name}
              />
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCatDialog(null)}>Cancel</Button>
          <Button
            variant='contained'
            onClick={saveCategories}
            disabled={catSaving || catLoading}>
            {catSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Accordion>
  );
}
